import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MenuItems } from '../../../shared/menu-items/menu-items';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { ApiService } from 'src/app/modules/api.service';
import { PersistenceService } from 'src/app/modules/general/services/persistence.service';
import { AuthenticationService } from 'src/app/authentication/authentication.service';
import { NotificationStateService } from 'src/app/modules/general/services/notification-state.service';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { AccordionAnchorDirective, AccordionLinkDirective, AccordionDirective } from 'src/app/shared/accordion';

interface UserData {
  Person?: {
    firstName?: string;
    infix?: string;
    lastName?: string;
  };
  [key: string]: unknown;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatMenuModule, MatIconModule, MatListModule, MatChipsModule, MatButtonModule, MatBadgeModule, RouterModule, AccordionAnchorDirective, AccordionLinkDirective, AccordionDirective]
})
export class AppSidebarComponent implements AfterViewInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  public mobileQuery!: MediaQueryList;
  public UserData: UserData;
  public urgentValuations = false;
  public notificationUnreadCount = 0;
  private urgentValuationsInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private router: Router,
    private apiService: ApiService,
    private persistence: PersistenceService,
    public menuItems: MenuItems,
    private authService: AuthenticationService,
    private notificationState: NotificationStateService,
    private cdr: ChangeDetectorRef
  ) {
    this.UserData = JSON.parse(localStorage.getItem('authUserInformation') || '{}');
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    // Always clear the interval when the component is destroyed to prevent memory leaks
    if (this.urgentValuationsInterval !== null) {
      clearInterval(this.urgentValuationsInterval);
    }
  }

  public ngAfterViewInit() {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(event => {
        this.markActiveMenuItems((event as NavigationEnd).url);
      });

    // Mark active menu items on component initialization
    this.markActiveMenuItems(window.location.pathname);

    this.notificationState.unreadCount
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.notificationUnreadCount = count;
        this.cdr.markForCheck();
      });

    // Menu counters are read from PersistenceService in the template; re-check when that state changes
    this.persistence.stateChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cdr.markForCheck());
  }

  public markActiveMenuItems(currentRoute: string): void {
    const currentPath = new URL(currentRoute, window.location.origin).pathname;
    // loop through all menu items and mark the active one
    const menuItems = document.querySelectorAll('.sidebar-menu-item');
    menuItems.forEach(menuItem => {
      const aTag = menuItem.querySelector('a');
      if (aTag) {
        const href = aTag.getAttribute('href');
        if (href) {
          const menuItemPath = new URL(href, window.location.origin).pathname;
          if (currentPath === menuItemPath) {
            menuItem.classList.add('active');
          } else {
            menuItem.classList.remove('active');
          }
        }
      }
    });

    // loop through all sub-menu items and mark the active ones
    const subMenuItems = document.querySelectorAll('.sub-item-a');
    subMenuItems.forEach(subMenuItem => {
      const aTag = subMenuItem.querySelector('a');
      if (aTag) {
        const href = aTag.getAttribute('href');
        const menuItemPath = href ? new URL(href, window.location.origin).pathname : '';
        if (currentPath === menuItemPath) {
          subMenuItem.classList.add('active');
        } else {
          subMenuItem.classList.remove('active');
        }
      }
    });
  }

  public checkForUrgentValuations() {
    this.apiService.getValuations(1, 1, undefined, JSON.stringify({ AND: [["isTreated", "eq", "false"], ["ValuationType.name", "eq", "urgent"], ["StatusType.name", "eq", "new"]] }), undefined).subscribe(res => {
      if (res.paginator.records > 0) {
        this.setUrgentCounter(res);
        this.urgentValuations = true;
      } else {
        this.setUrgentCounter(res);
        this.urgentValuations = false;
      }
      this.cdr.markForCheck();
    });

    this.apiService.getValuations(1, 1, undefined, JSON.stringify({ AND: [["ValuationType.name", "eq", "urgent"], ["StatusType.name", "eq", "new"]] }), undefined).subscribe(res => {
      if (res.paginator.records > 0) {
        this.setUrgentTotalCounter(res);
        this.urgentValuations = true;
      } else {
        this.setUrgentTotalCounter(res);
        this.urgentValuations = false;
      }
      this.cdr.markForCheck();
    });
  }

  private setUrgentCounter(res: { paginator: { records: number } }): void {
    const dashboardState = this.persistence.retrieveState('/general/dashboard');
    if (dashboardState) {
      const storageData = (dashboardState.storageData as Record<string, unknown>) || {};
      storageData.counterData = (storageData.counterData as Record<string, unknown> | undefined) || {};
      (storageData.counterData as Record<string, unknown>)['Spoed'] = res;
      this.persistence.updateState(storageData, '/general/dashboard');
    }
  }

  private setUrgentTotalCounter(res: { paginator: { records: number } }): void {
    const dashboardState = this.persistence.retrieveState('/general/dashboard');
    if (dashboardState) {
      const storageData = (dashboardState.storageData as Record<string, unknown>) || {};
      storageData.counterData = (storageData.counterData as Record<string, unknown> | undefined) || {};
      (storageData.counterData as Record<string, unknown>)['SpoedTotaal'] = res;
      this.persistence.updateState(storageData, '/general/dashboard');
    }
  }

  public logout() {
    this.authService.logout();
  }
}

